import { useStripe } from '@stripe/react-stripe-js';
import { StripeCardNumberElement } from '@stripe/stripe-js';
import { useTranslation } from 'next-i18next';
import { useCallback } from 'react';

import type { CheckoutV1 } from '@getjust/api-schema';

import { CardParam } from '$src/components/Card/IntegratedWrapper';
import { AnalyticsEventName, useTrackAnalytics } from '$src/hooks/client/useTrackAnalytics';
import { useErrorAtom } from '$src/hooks/state/useErrorAtom';

type CreateTokenParams = {
  firstname: string;
  lastname: string;
};

type StripeTokenError = {
  type: 'stripe_token_error';
  code: string | undefined;
  message: string;
};

const isStripeTokenError = (error: unknown): error is StripeTokenError =>
  typeof error === 'object' && error !== null && (error as StripeTokenError).type === 'stripe_token_error';

export const useCreateStripeToken = () => {
  const stripe = useStripe();
  const { track } = useTrackAnalytics();
  const { t, i18n } = useTranslation('common');
  const { onErrorChange } = useErrorAtom();

  const createToken = useCallback(
    async (
      cardElement: StripeCardNumberElement,
      { firstname, lastname }: CreateTokenParams,
    ): Promise<CardParam | null> => {
      try {
        const response = await stripe?.createToken(cardElement);

        if (response?.error) {
          throw {
            type: 'stripe_token_error',
            code: response.error.code,
            message: response.error.message ?? 'Unknown Stripe error',
          } satisfies StripeTokenError;
        }

        if (!response?.token) {
          throw {
            type: 'stripe_token_error',
            code: undefined,
            message: 'No token returned from Stripe',
          } satisfies StripeTokenError;
        }

        return {
          psp_type: 'stripe',
          id: response.token.card?.id ?? '',
          card: {
            ...((response.token.card ?? {}) as CheckoutV1.SourcesCreateSourceRequestBody['card'])!,
            name: `${firstname} ${lastname}`,
            token_id: response.token.id,
          },
        };
      } catch (error) {
        const errorCode = isStripeTokenError(error) ? error.code : undefined;
        const errorMessage = isStripeTokenError(error)
          ? error.message
          : error instanceof Error
            ? error.message
            : 'Unknown error';

        console.error('[useCreateStripeToken] Stripe token error:', errorMessage);

        track(AnalyticsEventName.CARD_TOKEN_CREATION_FAILED, {
          error: errorMessage,
          code: errorCode,
        });

        const stripeErrorCode = errorCode ?? 'generic_decline';
        const titleError = `errors.stripe.${stripeErrorCode}.title`;
        const sentenceError = `errors.stripe.${stripeErrorCode}.sentence`;

        onErrorChange({
          title: i18n.exists(titleError) ? t(titleError) : t('errors.stripe.generic_decline.title'),
          sentence: i18n.exists(sentenceError)
            ? t(sentenceError)
            : t('errors.stripe.generic_decline.sentence'),
        });

        return null;
      }
    },
    [stripe, track, t, i18n, onErrorChange],
  );

  return { createToken };
};
